// NobleCommandBar - Industrial Command Center Navigation
// Mobile: Bottom bar (thumb-arc optimized)
// Desktop: Left sidebar with icons + labels
// Features: Active states, badges, smooth transitions

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge, Tooltip } from 'antd';
import {
  DashboardOutlined,
  ProjectOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  ToolOutlined,
  SettingOutlined,
  FileTextOutlined,
  BellOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { NobleColors, NobleSpacing, NobleShadows, NobleTypography } from '../../../theme/noble-tokens';

export interface CommandBarItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  badge?: number | string;
  onClick?: () => void;
  disabled?: boolean;
}

export interface NobleCommandBarProps {
  items?: CommandBarItem[];
  variant?: 'mobile' | 'desktop';
  showLabels?: boolean;
  onItemClick?: (item: CommandBarItem) => void;
}

// Default navigation items
const defaultItems: CommandBarItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: <DashboardOutlined />,
    path: '/',
  },
  {
    id: 'projects',
    label: 'Projects',
    icon: <ProjectOutlined />,
    path: '/projects',
    badge: 3,
  },
  {
    id: 'inventory',
    label: 'Inventory',
    icon: <ShoppingCartOutlined />,
    path: '/inventory',
  },
  {
    id: 'quality',
    label: 'Quality',
    icon: <ToolOutlined />,
    path: '/quality',
    badge: 5,
  },
  {
    id: 'team',
    label: 'Team',
    icon: <TeamOutlined />,
    path: '/hrms',
  },
];

// Styled Components
const MobileCommandBarContainer = styled(motion.div)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 72px;
  background: linear-gradient(180deg, ${NobleColors['level-1-light']} 0%, ${NobleColors['level-2-light']} 100%);
  border-top: 2px solid ${NobleColors['steel-gray-base']};
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: ${NobleSpacing.xs} ${NobleSpacing.md};
  z-index: 1000;
  backdrop-filter: blur(8px);

  /* Thumb arc optimization - items curve slightly up */
  border-radius: 24px 24px 0 0;

  @media (min-width: 769px) {
    display: none; /* Hide on desktop */
  }
`;

const DesktopCommandBarContainer = styled(motion.div)`
  position: fixed;
  left: 0;
  top: 64px; /* Below header */
  bottom: 0;
  width: 240px;
  background: linear-gradient(180deg, ${NobleColors['level-1-light']} 0%, ${NobleColors['level-2-light']} 100%);
  border-right: 2px solid ${NobleColors['steel-gray-base']};
  box-shadow: 2px 0 16px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  padding: ${NobleSpacing.lg} 0;
  z-index: 999;
  overflow-y: auto;

  @media (max-width: 768px) {
    display: none; /* Hide on mobile */
  }
`;

const CommandBarItemWrapper = styled(motion.button)<{ $active: boolean; $variant: 'mobile' | 'desktop' }>`
  display: flex;
  flex-direction: ${({ $variant }) => ($variant === 'mobile' ? 'column' : 'row')};
  align-items: center;
  justify-content: center;
  gap: ${({ $variant }) => ($variant === 'mobile' ? NobleSpacing.xxs : NobleSpacing.md)};
  padding: ${({ $variant }) => ($variant === 'mobile' ? `${NobleSpacing.xs} ${NobleSpacing.sm}` : `${NobleSpacing.md} ${NobleSpacing.lg}`)};
  background: ${({ $active }) => ($active ? NobleColors['command-blue-base'] : 'transparent')};
  color: ${({ $active }) => ($active ? 'white' : NobleColors['steel-gray-dark'])};
  border: none;
  border-radius: ${({ $variant }) => ($variant === 'mobile' ? '12px' : '8px')};
  cursor: pointer;
  transition: all 0.2s ease-out;
  position: relative;
  min-width: ${({ $variant }) => ($variant === 'mobile' ? '64px' : 'auto')};
  width: ${({ $variant }) => ($variant === 'mobile' ? 'auto' : '100%')};
  text-align: left;
  font-family: ${NobleTypography['font-primary']};
  font-size: ${NobleTypography['body-small-desktop-size']};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  margin: ${({ $variant }) => ($variant === 'desktop' ? `0 ${NobleSpacing.md} ${NobleSpacing.xs} ${NobleSpacing.md}` : '0')};

  /* Icon styling */
  .anticon {
    font-size: ${({ $variant }) => ($variant === 'mobile' ? '24px' : '20px')};
  }

  /* Active state indicator (left border on desktop) */
  ${({ $active, $variant }) =>
    $active &&
    $variant === 'desktop' &&
    `
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      background: ${NobleColors['command-blue-base']};
      border-radius: 0 4px 4px 0;
    }
  `}

  /* Active state indicator (bottom border on mobile) */
  ${({ $active, $variant }) =>
    $active &&
    $variant === 'mobile' &&
    `
    &::after {
      content: '';
      position: absolute;
      bottom: -4px;
      left: 50%;
      transform: translateX(-50%);
      width: 32px;
      height: 3px;
      background: white;
      border-radius: 3px 3px 0 0;
    }
  `}

  /* Hover states */
  &:hover:not(:disabled) {
    background: ${({ $active }) =>
      $active ? NobleColors['command-blue-light'] : `rgba(${NobleColors['command-blue-base-rgb'] || '10, 116, 218'}, 0.1)`};
    transform: ${({ $variant }) => ($variant === 'mobile' ? 'translateY(-2px)' : 'translateX(2px)')};
  }

  /* Active press effect */
  &:active:not(:disabled) {
    transform: ${({ $variant }) => ($variant === 'mobile' ? 'translateY(1px)' : 'translateX(0px)')};
    box-shadow: ${({ $active }) => ($active ? `inset 0 2px 4px rgba(0,0,0,0.2)` : 'none')};
  }

  /* Disabled state */
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const CommandBarLabel = styled.span<{ $variant: 'mobile' | 'desktop' }>`
  font-size: ${({ $variant }) => ($variant === 'mobile' ? '11px' : '14px')};
  font-weight: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: ${({ $variant }) => ($variant === 'mobile' ? '100%' : '140px')};
`;

const FloatingActionButton = styled(motion.button)`
  position: absolute;
  top: ${({ theme }) => theme === 'mobile' ? '-28px' : 'auto'};
  bottom: ${({ theme }) => theme === 'desktop' ? NobleSpacing.lg : 'auto'};
  left: 50%;
  transform: translateX(-50%);
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${NobleColors['command-blue-base']} 0%, ${NobleColors['command-blue-dark']} 100%);
  color: white;
  border: 4px solid ${NobleColors['level-0-light']};
  box-shadow: ${NobleShadows['elevation-3']};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1001;
  font-size: 24px;
  transition: all 0.2s ease-out;

  &:hover {
    transform: translateX(-50%) scale(1.1);
    box-shadow: ${NobleShadows['elevation-4']};
  }

  &:active {
    transform: translateX(-50%) scale(0.95);
  }

  @media (min-width: 769px) {
    display: ${({ theme }) => theme === 'mobile' ? 'none' : 'flex'};
    position: relative;
    left: auto;
    transform: none;
    margin: ${NobleSpacing.lg} auto 0 auto;

    &:hover {
      transform: scale(1.1);
    }

    &:active {
      transform: scale(0.95);
    }
  }
`;

const CommandBarDivider = styled.div`
  height: 1px;
  background: ${NobleColors['steel-gray-base']};
  margin: ${NobleSpacing.md} ${NobleSpacing.lg};
  opacity: 0.3;
`;

// Main Component
export const NobleCommandBar: React.FC<NobleCommandBarProps> = ({
  items = defaultItems,
  variant: propVariant,
  showLabels = true,
  onItemClick,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const variant = propVariant || (isMobile ? 'mobile' : 'desktop');

  // Responsive detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleItemClick = (item: CommandBarItem) => {
    if (item.disabled) return;

    if (item.onClick) {
      item.onClick();
    } else if (item.path) {
      navigate(item.path);
    }

    onItemClick?.(item);
  };

  const isActive = (item: CommandBarItem) => {
    // Check if current path matches item path
    if (item.path === '/' && location.pathname === '/') return true;
    if (item.path !== '/' && location.pathname.startsWith(item.path)) return true;
    return false;
  };

  const renderCommandBarItem = (item: CommandBarItem) => {
    const active = isActive(item);

    const itemContent = (
      <CommandBarItemWrapper
        key={item.id}
        $active={active}
        $variant={variant}
        onClick={() => handleItemClick(item)}
        disabled={item.disabled}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <Badge count={item.badge} offset={[8, -4]} size="small">
          {item.icon}
        </Badge>
        {showLabels && <CommandBarLabel $variant={variant}>{item.label}</CommandBarLabel>}
      </CommandBarItemWrapper>
    );

    // Wrap with tooltip on desktop when labels are hidden
    if (variant === 'desktop' && !showLabels) {
      return (
        <Tooltip key={item.id} title={item.label} placement="right">
          {itemContent}
        </Tooltip>
      );
    }

    return itemContent;
  };

  const renderFloatingActionButton = () => (
    <FloatingActionButton
      theme={variant}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      onClick={() => console.log('Quick create action')}
      aria-label="Quick Create"
    >
      <PlusOutlined />
    </FloatingActionButton>
  );

  // Mobile bottom bar
  if (variant === 'mobile') {
    return (
      <MobileCommandBarContainer
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        exit={{ y: 100 }}
        transition={{ type: 'spring', damping: 20 }}
      >
        {items.slice(0, 5).map(renderCommandBarItem)}
        {renderFloatingActionButton()}
      </MobileCommandBarContainer>
    );
  }

  // Desktop left sidebar
  return (
    <DesktopCommandBarContainer
      initial={{ x: -240 }}
      animate={{ x: 0 }}
      exit={{ x: -240 }}
      transition={{ type: 'spring', damping: 20 }}
    >
      {items.map(renderCommandBarItem)}
      <CommandBarDivider />
      {renderFloatingActionButton()}
    </DesktopCommandBarContainer>
  );
};

// Pre-configured Command Bars
export const MobileCommandBar: React.FC<Omit<NobleCommandBarProps, 'variant'>> = (props) => (
  <NobleCommandBar {...props} variant="mobile" />
);

export const DesktopCommandBar: React.FC<Omit<NobleCommandBarProps, 'variant'>> = (props) => (
  <NobleCommandBar {...props} variant="desktop" />
);

